/**
 * Bundled language list for the multilingual description editor.
 *
 * This is a static, offline list - no API call, no network dependency. It
 * exists so a contributor can pick the language a description is written in
 * and see it named in that language rather than in English.
 *
 * Ordering is deliberate: Indic languages lead, then the eight UI locales,
 * then other common Commons content languages. The picker preserves this
 * order, so the languages most contributors here need are the first things
 * they see before typing anything.
 *
 * The list is not exhaustive and does not need to be - the description
 * editor accepts any code the picker offers, and Commons renders
 * `{{<code>|1=...}}` for any language template that exists there.
 */

import raw from "./languages.json"

export interface LanguageEntry {
  /** Language code as used in the Commons `{{<lang>}}` description template. */
  code: string
  /** The language's name in that language. */
  native: string
  /**
   * Set when Wikibase will not accept this code as a structured-data
   * caption language, even though Commons has a {{lang}} template for it.
   * Such a language is offered for descriptions but hidden from the caption
   * picker. Verified against action=paraminfo&modules=wbsetlabel.
   */
  noCaption?: true
}

export const LANGUAGES: LanguageEntry[] = raw as LanguageEntry[]

/**
 * The subset Wikibase accepts as a caption language. Commons stores
 * captions as MediaInfo labels, and its label-language list is not the
 * same as its set of {{lang}} description templates.
 */
export const CAPTION_LANGUAGES: LanguageEntry[] = LANGUAGES.filter(l => !l.noCaption)

const BY_CODE = new Map(LANGUAGES.map(l => [l.code, l]))

/**
 * The language's own name, or the bare code when it is not in the bundled
 * list. Used for row labels, so an unrecognised code still renders as
 * something rather than as an empty label.
 */
export function nativeName(code: string): string {
  return BY_CODE.get(code)?.native ?? code
}

export function isKnownLanguage(code: string): boolean {
  return BY_CODE.has(code)
}

/** Whether Commons will accept this code as a caption (MediaInfo label). */
export function isCaptionLanguage(code: string): boolean {
  const entry = BY_CODE.get(code)
  return !!entry && !entry.noCaption
}

/**
 * Filter the list by a query matching either the code or the native name.
 *
 * Exact code matches sort first, then code prefixes, then native-name
 * prefixes, then anything else containing the query - so typing "ta" puts
 * Tamil above Tagalog, and typing "বাং" finds Bengali by its own name.
 * An empty query returns the head of the list in its curated order.
 */
export interface SearchOptions {
  /** Restrict results to languages Commons accepts as caption languages. */
  captionsOnly?: boolean
}

export function searchLanguages(
  query: string, limit = 60, opts: SearchOptions = {}
): LanguageEntry[] {
  const pool = opts.captionsOnly ? CAPTION_LANGUAGES : LANGUAGES
  const q = query.trim().toLowerCase()
  if (!q) return pool.slice(0, limit)

  const scored: Array<{ entry: LanguageEntry; rank: number }> = []
  for (const entry of pool) {
    const code = entry.code.toLowerCase()
    const native = entry.native.toLowerCase()
    let rank = -1
    if (code === q) rank = 0
    else if (code.startsWith(q)) rank = 1
    else if (native.startsWith(q)) rank = 2
    else if (code.includes(q)) rank = 3
    else if (native.includes(q)) rank = 4
    if (rank >= 0) scored.push({ entry, rank })
  }

  // Stable within a rank, so the curated order still shows through.
  scored.sort((a, b) => a.rank - b.rank)
  return scored.slice(0, limit).map(s => s.entry)
}
